import React, { useEffect, useRef, useState } from "react";
import { FolderLock, Upload, Download, Trash2, FileText, Camera } from "lucide-react";
import { toast } from "sonner";
import api, { fmtDay, formatErr } from "../../lib/api";
import { isNative, takePhoto, nativeSaveBlob } from "../../native";
import { Loading, EmptyState, PageHeader } from "../../components/Shared";

const CATS = ["prescription", "lab_report", "discharge_summary", "insurance", "care_document", "other"];

export default function Documents() {
  const [docs, setDocs] = useState(null);
  const [category, setCategory] = useState("prescription");
  const [parentId, setParentId] = useState("");
  const [parents, setParents] = useState([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();
  const load = () => api.get("/family/documents").then((r) => setDocs(r.data));
  useEffect(() => { load().catch(() => setDocs([])); api.get("/family/parents").then((r) => setParents(r.data)).catch(() => {}); }, []);
  const uploadFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", category);
      fd.append("parent_id", parentId);
      await api.post("/family/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded securely"); load();
    } catch (err) { toast.error(formatErr(err)); }
    setBusy(false);
  };
  const upload = async (e) => {
    const file = e.target.files?.[0];
    await uploadFile(file);
    e.target.value = "";
  };
  const capture = async () => {
    try {
      const file = await takePhoto();
      if (file) await uploadFile(file);
    } catch { /* user cancelled or permission denied */ }
  };
  const download = async (doc) => {
    try {
      const r = await api.get(`/family/documents/${doc.id}/file`, { responseType: "blob" });
      if (isNative()) {
        await nativeSaveBlob(r.data, doc.name);
        toast.success("Saved to your device Documents folder");
        return;
      }
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = doc.name; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatErr(err)); }
  };
  const remove = async (doc) => {
    try { await api.delete(`/family/documents/${doc.id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatErr(err)); }
  };
  if (!docs) return <Loading />;
  return (
    <div data-testid="documents-page">
      <PageHeader title="Document Vault" sub="Prescriptions, reports and care documents — stored securely, never exposed via public links." />
      <div className="p1-card mb-6 flex flex-wrap items-end gap-3 p-5" data-testid="upload-block">
        <div><label className="p1-label">Category</label>
          <select className="p1-input !w-48" value={category} onChange={(e) => setCategory(e.target.value)} data-testid="upload-category">
            {CATS.map((c) => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
          </select></div>
        {parents.length > 1 && (
          <div><label className="p1-label">Parent</label>
            <select className="p1-input !w-48" value={parentId} onChange={(e) => setParentId(e.target.value)} data-testid="upload-parent">
              <option value="">— Select parent —</option>
              {parents.map((p) => <option key={p.id} value={p.id}>{p.label ? `${p.label} — ` : ""}{p.name}</option>)}
            </select></div>
        )}
        <input ref={fileRef} type="file" className="hidden" onChange={upload} data-testid="upload-input" />
        <button onClick={() => fileRef.current.click()} disabled={busy} className="p1-btn-primary !px-5 !py-2.5" data-testid="upload-btn">
          <Upload className="h-4 w-4" /> {busy ? "Uploading…" : "Upload Document"}
        </button>
        {isNative() && (
          <button onClick={capture} disabled={busy} className="p1-btn-outline !px-5 !py-2.5" data-testid="upload-camera-btn">
            <Camera className="h-4 w-4" /> Take Photo
          </button>
        )}
        <p className="text-xs text-stone-400">Max 10 MB. Access is permission-controlled and logged.</p>
      </div>
      {docs.length === 0 ? <EmptyState icon={FolderLock} title="Vault is empty" body="Upload prescriptions, lab reports, discharge summaries and insurance documents." /> : (
        <div className="space-y-3">
          {docs.map((d) => (
            <div key={d.id} className="p1-card flex items-center justify-between gap-3 p-4" data-testid={`doc-row-${d.id}`}>
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-900/5 text-emerald-900"><FileText className="h-5 w-5" /></span>
                <div><p className="text-sm font-semibold text-stone-800">{d.name}</p>
                  <p className="text-xs capitalize text-stone-400">{d.category.replace(/_/g, " ")} · {fmtDay(d.at)}{d.demo ? " · demo record" : ""}</p></div>
              </div>
              <div className="flex gap-1">
                <button onClick={() => download(d)} className="rounded-full p-2 text-stone-500 hover:bg-stone-100" aria-label="Download" data-testid={`doc-download-${d.id}`}><Download className="h-4 w-4" /></button>
                <button onClick={() => remove(d)} className="rounded-full p-2 text-stone-400 hover:text-red-600" aria-label="Delete" data-testid={`doc-delete-${d.id}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
